/** @format */

import React, { FC, useState, useRef } from 'react'
import styled from 'styled-components'
import { useAsync, useAsyncFn } from 'react-use'
import AlarmReciver from 'src/components/AlarmReciver'
import AlarmCallBack from 'src/components/AlarmCallBack'
import AlarmCallBackTooltip from 'src/components/AlarmCallBack/Tooltip'
import {
  _$,
  Switch,
  Button,
  Drawer,
  Form,
  FormItem,
  Radio,
  Transfer,
  Message
} from '@king-design/react'
import Api from 'src/api/ApiCloud'
import { request } from 'src/utils'
import { isNumber } from 'lodash'

interface CloudAlarmProps {
  className?: string
  network: 'ib' | 'rock'
}

const CloudAlarm: FC<CloudAlarmProps> = ({ className, network }) => {
  // 抽屉
  const [visible, setVisible] = useState<boolean>(false)
  const [formValues, setFormValues] = useState<any>({})
  const contactRef = useRef<any>()
  const callbackRef = useRef<any>()
  const formRef = useRef<any>()

  const { value: event_list } = useAsync(async () => {
    if (network === 'ib') {
      const result = await request(Api.getUFMEvents, {})
      return (
        result?.data?.ListEventsResult?.Data?.map(({ EventName }) => ({
          label: EventName,
          key: EventName
        })) || []
      )
    } else if (network === 'rock') {
      const result = await request(Api.getNCEEvents, {})
      return (
        result?.data?.ListIssueResult?.Data?.map(({ EventName, EventNameLabel }) => ({
          label: EventNameLabel,
          key: EventName
        })) || []
      )
    }
  }, [network])

  useAsync(async () => {
    if (!visible) {
      if (network === 'ib') {
        const result = await request(Api.getUFMAlarmSubscribe, {})
        if (result?.data?.DescribeEventSubscribeResult?.Data) {
          const { EventNames, UserNotice } = result?.data?.DescribeEventSubscribeResult?.Data
          const values = {
            ...result?.data?.DescribeEventSubscribeResult?.Data,
            EventType: EventNames[0] === '*' ? 1 : 2,
            EventNames: EventNames.filter(item => item !== '*'),
            UserNotice: UserNotice?.map(({ NoticeWay, ReceiverType, ReceiverID }) => ({
              contactWay: NoticeWay,
              contactFlag: ReceiverType,
              contactId: ReceiverID
            }))
          }
          setFormValues(values)
          return values
        }
      } else if (network === 'rock' && event_list?.length) {
        const result = await request(Api.getNCEAlarmSubscribe, {})
        if (result?.data?.DescribeIssueSubscribeResult?.Data) {
          const { EventNames, UserNotice } = result?.data?.DescribeIssueSubscribeResult?.Data
          const values = {
            ...result?.data?.DescribeIssueSubscribeResult?.Data,
            EventType: EventNames[0] === '*' ? 1 : 2,
            EventNames: EventNames.filter(item => item !== '*').map(item => {
              const find = event_list.find(event => event.key === item)
              return find || {}
            }),
            UserNotice: UserNotice?.map(({ NoticeWay, ReceiverType, ReceiverID }) => ({
              contactWay: NoticeWay,
              contactFlag: ReceiverType,
              contactId: ReceiverID
            }))
          }
          setFormValues(values)
          return values
        }
      }
    }
  }, [visible, network, event_list])

  // 开启/禁用告警通知
  const [{ loading: switch_loading }, switch_fetch] = useAsyncFn(
    async (value: boolean) => {
      if (network === 'ib') {
        if (value) {
          const result = await request(Api.enableUFMALarm, { method: 'POST' })
          if (result?.data?.EnableEventSubscribeResult?.Data === 'SUCCESS') {
            Message.success(_$('启用成功'))
          }
        } else {
          const result = await request(Api.disabledUFMALarm, { method: 'POST' })
          if (result?.data?.DisableEventSubscribeResult?.Data === 'SUCCESS') {
            Message.success(_$('禁用成功'))
          }
        }
      } else if (network === 'rock') {
        if (value) {
          const result = await request(Api.enableNCEALarm, { method: 'POST' })
          if (result?.data?.EnableIssueSubscribeResult?.Data === 'SUCCESS') {
            Message.success(_$('启用成功'))
          }
        } else {
          const result = await request(Api.disabledNCEALarm, { method: 'POST' })
          if (result?.data?.DisableIssueSubscribeResult?.Data === 'SUCCESS') {
            Message.success(_$('禁用成功'))
          }
        }
      }
    },
    [network]
  )

  const submit = async () => {
    const validate = await formRef?.current?.validate()
    if (validate) {
      // 告警名称
      const { EventNames } = formValues
      // 告警联系人
      const users = contactRef.current && contactRef.current.getRecivers()
      const contactMap = users?.length
        ? users.map(({ contactWay, contactFlag, contactId }) => ({
            NoticeWay: contactWay,
            ReceiverType: contactFlag,
            ReceiverID: contactId
          }))
        : []
      // 告警回调
      const callback = callbackRef.current && (await callbackRef.current.getUrl())

      if (network === 'ib') {
        const result = await request(Api.setUFMAlarmSubscribe, {
          method: 'POST',
          data: {
            EventNames:
              EventNames?.length === event_list?.length || formValues?.EventType === 1
                ? ['*']
                : EventNames,
            UserNotice: contactMap,
            URLNotice: callback
          }
        })
        if (isNumber(result?.data?.PutEventSubscribeResult?.Data?.SubscribeID)) {
          Message.success(_$('编辑成功'))
          setVisible(false)
          setFormValues({
            ...formValues,
            UserNotice: users,
            URLNotice: callback
          })
        }
      } else if (network === 'rock') {
        const result = await request(Api.setNCEAlarmSubscribe, {
          method: 'POST',
          data: {
            EventNames:
              EventNames?.length === event_list?.length || formValues?.EventType === 1
                ? ['*']
                : EventNames.map(({ key }) => key),
            UserNotice: contactMap,
            URLNotice: callback
          }
        })
        if (isNumber(result?.data?.PutIssueSubscribeResult?.Data?.SubscribeID)) {
          Message.success(_$('编辑成功'))
          setVisible(false)
          setFormValues({
            ...formValues,
            UserNotice: users,
            URLNotice: callback
          })
        }
      }
    }
  }

  return (
    <div className={className} id="cloud_alarm_drawer">
      <span className="form_item_label">{_$('告警通知')}</span>
      <Switch
        on={_$('开启')}
        off={_$('关闭')}
        width="62"
        height="24"
        value={formValues.Enabled === 1}
        loading={switch_loading}
        onChangeValue={value => {
          switch_fetch(value)
          setFormValues({
            ...formValues,
            Enabled: value ? 1 : 2
          })
        }}
      />
      {formValues.Enabled === 1 && (
        <Button
          onClick={() => {
            setVisible(true)
          }}
        >
          {_$('告警设置')}
        </Button>
      )}
      <Drawer
        value={visible}
        width={800}
        container={() => document.getElementById('cloud_alarm_drawer')}
        ok={() => {
          submit()
        }}
        onChangeValue={show => {
          if (show) {
          } else {
            setVisible(show)
          }
        }}
        title={_$('告警设置')}
      >
        <Form starOnRequired={true} ref={formRef} labelWidth={85}>
          <FormItem label={_$('事件名称')} value={formValues?.EventType} rules={{ required: true }}>
            <Radio
              name="instanceRadio"
              trueValue={1}
              value={formValues.EventType}
              onChangeValue={value => {
                setFormValues({
                  ...formValues,
                  EventType: value
                })
              }}
            >
              {_$('全部事件')}
            </Radio>
            <Radio
              name="instanceRadio"
              trueValue={2}
              value={formValues.EventType}
              onChangeValue={value => {
                setFormValues({
                  ...formValues,
                  EventType: value
                })
              }}
            >
              {_$('自选事件')}
              {formValues.EventType === 2 ? (
                <span>
                  (<span className="primary-color">{formValues?.EventNames?.length}</span>)
                </span>
              ) : null}
            </Radio>
          </FormItem>
          {formValues.EventType === 2 && event_list?.length ? (
            <FormItem
              value={formValues?.EventNames}
              label={_$('自选事件')}
              rules={{
                required: true,
                checkIns(value) {
                  return !!value?.length
                }
              }}
            >
              <Transfer
                filterable
                data={event_list}
                value={formValues?.EventNames}
                onChangeValue={value => {
                  setFormValues({
                    ...formValues,
                    EventNames: value
                  })
                }}
              ></Transfer>
            </FormItem>
          ) : (
            ''
          )}
          <FormItem label={_$('告警联系人')}>
            <AlarmReciver ref={contactRef} defaultValue={formValues?.UserNotice} />
          </FormItem>
          <FormItem slotLabel={<AlarmCallBackTooltip />}>
            <AlarmCallBack ref={callbackRef} defaultValue={formValues?.URLNotice} />
          </FormItem>
        </Form>
      </Drawer>
    </div>
  )
}

export default styled(CloudAlarm)`
  .k-radio {
    margin-right: 16px;
  }
`
